import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, CircularProgress, DataGrid, Paper, Typography } from 'components/AORedesign';
import { GET_BILLING_PAYMENT_NSF_PAYMENTS } from '../../../../constants/api';
import BackToPaymentManagementButton from '../BackToPaymentManagementButton';
import PaymentConfirmation from './PaymentConfirmation';
import PaymentValidation from './PaymentValidation';
import {
  handleEditOnClickFromValidation as _handleEditOnClickFromValidation,
  handleOnClickEnterPaymentValidation as _handleOnClickEnterPaymentValidation,
  handleOnClickReset as _handleOnClickReset,
  handleOnClickSubmitPayment as _handleOnClickSubmitPayment,
  onSelectionModelChange as _onSelectionModelChange,
  fetchCurrentPayments,
  reportInsufficientFundsCols,
} from './ReportInsufficientFunds.api';

const screenMappings = {
  start: `BillingServicesPaymentManagementReportInsufficientFundsPanel`,
  paymentValidation: `BillingServicesPaymentManagementReportInsufficientFundsPanelPaymentValidation`,
  paymentConfirmation: `BillingServicesPaymentManagementReportInsufficientFundsPanelPaymentConfirmation`,
};

const BillingServicesPaymentManagementReportInsufficientFunds = ({ resetFlow }) => {
  const [currentScreen, setCurrentScreen] = useState(screenMappings.start);
  const [selectionModel, setSelectionModel] = useState([]);
  const [paymentListData, setPaymentListData] = useState({
    loading: true,
    paymentList: null,
    selectedRow: {},
    alert: {
      message: null,
      severity: 'error',
      isVisible: false,
    },
  });

  useEffect(() => {
    if (currentScreen === screenMappings.start) {
      fetchCurrentPayments(setPaymentListData, GET_BILLING_PAYMENT_NSF_PAYMENTS);
    }
  }, [currentScreen]);

  const selectButtonDisabled = selectionModel.length !== 1;
  const paymentListLoading = paymentListData.loading;
  const paymentList = paymentListData.paymentList;
  const paymentListAlertMessage = paymentListData.alert.message;
  const paymentListAlertIsVisible = paymentListData.alert.isVisible;
  const paymentValidationData = {
    changeDate: paymentListData.selectedRow.payDateCreated,
    originalAmount: paymentListData.selectedRow.originalAmount,
    accountNumber: paymentListData.selectedRow.accountNumber,
    accountName: paymentListData.selectedRow.accountName,
    payIndex: paymentListData.selectedRow.payIndex,
  };

  const handleClickBackToPaymentManagement = () => resetFlow();
  const onSelectionModelChange = (selection) =>
    _onSelectionModelChange(paymentListData.paymentList, selection, selectionModel, setSelectionModel, setPaymentListData);
  const handleOnClickReset = () => _handleOnClickReset(setSelectionModel);
  const handleOnClickEnterPaymentValidation = () => _handleOnClickEnterPaymentValidation(screenMappings, setCurrentScreen);
  const handleEditOnClickFromValidation = () => _handleEditOnClickFromValidation(screenMappings, setCurrentScreen, setSelectionModel);
  const handleOnClickSubmitPayment = () => _handleOnClickSubmitPayment(paymentListData.selectedRow, setPaymentListData, screenMappings, setCurrentScreen);

  const handleClickGoToNSFPayment = () => {
    setCurrentScreen(screenMappings.start);
  };

  return (
    <>
      {currentScreen === screenMappings.start && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Box mb={2}>
            <Typography variant="h5">Billing Payment Service</Typography>
          </Box>

          <Typography>Select an account to Report an NSF Item</Typography>

          {paymentListAlertIsVisible && <Alert severity="error">{paymentListAlertMessage}</Alert>}

          <Box mt={2}>
            {paymentListLoading ? (
              <CircularProgress />
            ) : (
              <DataGrid
                autoHeight
                pageSize={10}
                checkboxSelection
                noResultsMessage="No results."
                rows={paymentList}
                columns={reportInsufficientFundsCols}
                selectionModel={selectionModel}
                loading={paymentListLoading}
                onSelectionModelChange={onSelectionModelChange}
              />
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleClickBackToPaymentManagement} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={handleOnClickReset} style={{ marginRight: 8 }}>
              Reset
            </Button>
            <Button disabled={selectButtonDisabled} onClick={handleOnClickEnterPaymentValidation}>
              Select
            </Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.paymentValidation && (
        <>
          <Box mb={2}>
            <Typography variant="h5">Billing Payment Service</Typography>
          </Box>

          <Box mb={2}>
            <Typography>Payment Validation</Typography>
          </Box>

          <Paper>
            <PaymentValidation paymentValidationData={paymentValidationData} />
          </Paper>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleEditOnClickFromValidation} style={{ marginRight: 8 }}>
              Edit
            </Button>
            <Button variant="outlined" onClick={handleClickBackToPaymentManagement} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleOnClickSubmitPayment}>Submit</Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.paymentConfirmation && (
        <Box>
          <Box mb={2}>
            <Typography variant="h5">Billing Payment Service</Typography>
          </Box>

          <Box mb={2}>
            <Typography>Payment Confirmation</Typography>
          </Box>

          <Paper>
            <PaymentConfirmation paymentValidationData={paymentValidationData} />
          </Paper>

          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="text" color="secondary" onClick={handleClickGoToNSFPayment}>
              Go to NSF Payments
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

BillingServicesPaymentManagementReportInsufficientFunds.propTypes = {
  handleClickGoToAdjustPayment: PropTypes.func,
  handleClickGoToPostPayment: PropTypes.func,
  handleClickGoToNSFPayment: PropTypes.func,
  resetFlow: PropTypes.func,
};

export default BillingServicesPaymentManagementReportInsufficientFunds;
